import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Folder } from 'lucide-react';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const CitizenActionPage = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/posts/list?category=citizen-action&limit=6`)
      .then(res => setPosts(res.data.posts || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="citizen-action-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero */}
      <div className="relative pt-[50px]">
        <div className="absolute inset-0" style={{
          backgroundColor: '#9E1B32',
          backgroundImage: "url('https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-66-1024x683.jpg')",
          backgroundSize: 'cover', backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />
        <div className="relative z-10 py-20 sm:py-28">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Citizen Action
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 max-w-5xl">

          {/* Intro */}
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-[#9B1E37] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Secure Elections Matter to Marylanders
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              We are on a mission to prove that secure and accurate elections are something Marylanders believe necessary for a government of the people, by the people. There are many ways you can get involved and make a difference.
            </p>
          </motion.div>

          {/* Sign the Petition CTA */}
          <motion.div
            className="rounded-xl overflow-hidden mb-16"
            style={{ backgroundColor: '#9E1B32' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="px-8 py-10 sm:px-12 sm:py-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Sign the Petition
              </h2>
              <p className="text-white/85 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Join thousands of Maryland citizens demanding transparent, lawful, and trustworthy elections. Your signature sends a powerful message to our elected officials that election integrity matters.
              </p>
              <Link to="/sign-the-petition">
                <Button
                  size="lg"
                  className="px-10 py-6 text-lg font-bold tracking-wider"
                  style={{ backgroundColor: '#F6BF58', color: '#1a1a1a' }}
                  data-testid="petition-cta"
                >
                  SIGN THE PETITION
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Citizen Action Blog Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Get Involved</h2>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mb-8"
              initial={{ scaleX: 0, originX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.map((post, index) => {
              const date = post.published_at ? new Date(post.published_at) : null;
              return (
                <motion.article
                  key={post.id}
                  className="bg-white rounded-lg overflow-hidden shadow-md group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative overflow-hidden">
                      {post.featured_image ? (
                        <img src={post.featured_image} alt={post.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-[#9E1B32] to-[#6B1029] flex items-center justify-center">
                          <span className="text-white/30 text-6xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>STV</span>
                        </div>
                      )}
                      {date && (
                        <div className="absolute bottom-0 left-0 bg-[#9E1B32] text-white px-4 py-2">
                          <span className="block text-xl font-bold">{date.toLocaleDateString('en-US', { day: '2-digit' })}</span>
                          <span className="text-sm">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                        <Folder size={14} />
                        <span>Citizen Action</span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#9E1B32] transition-colors mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      {post.excerpt && <p className="text-slate-600 text-sm line-clamp-2 mb-4">{post.excerpt}</p>}
                      <span className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                        READ MORE <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>

        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default CitizenActionPage;

